import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import { DialogContent, DialogTitle, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EventEHRRadioButton from "../../../../component-lib/Molecules/radioButton/EventEHRRadioButton";
import Button from "../../../../component-lib/Molecules/button/Button";
import TransferOffPropertiesForm from "../../../../forms/TransferOffPropertiesForm";
import TransferToJHCForm from "../../../../forms/TransferToJHCForm";
import TransferToClinicLocationForm from "../../../../forms/TransferToBaseCampForm";
import { useDispatch, useSelector } from "react-redux";
import { postTransferPatient } from "../../patientSaga";
import { componentKey, setBundleOrderData } from "../../patientSlice";
import Input from "../../../../component-lib/Atoms/Input/Input";
import SearchSelectDropdown from "../../../../component-lib/Atoms/SearchSelectDropdown/EventEHRSearchSelectDropdown";
import TemplateService from "../../../../services/TemplateService";
import BundleOrderModal from "./BundleOrderModal";
import BundleOrderDisplay from "./BundelOrderDisplay";
import Heading from "../../../../component-lib/Atoms/heading/EventEHRHeading";
import { RESOLUTION_OPTIONS } from "../listeningEarConstants";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: "16px 24px",
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiPaper-root": {
    width: "708px",
    maxWidth: "none",
  },
}));

interface Template {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly type: string;
  readonly status: string;
}

export interface BundleOrderData {
  readonly conditions: Array<{
    readonly type?: string;
    readonly conditionName?: string;
    readonly medicationName?: string;
    readonly frequency?: string;
    readonly quantity?: string | number;
    readonly name?: string;
    readonly note?: string;
  }>;
  readonly vitals: Array<{
    readonly vitalName: string;
    readonly medicationName?: string;
    readonly frequency: string;
  }>;
}

interface DispositionDialogProps {
  readonly open: boolean;
  readonly visitNoteId?: string;
  readonly patientId?: string;
  readonly isOffsite?: boolean;
  // When true (Listening Ear only), the dialog shows the Resolution options
  // instead of the discharge/transfer options. Defaults to false so all other
  // callers keep their existing behavior.
  readonly isResolution?: boolean;
  readonly handleDischargeClick?: (type: string, data: string) => void;
  readonly handleResolutionSubmit?: (resolution: string) => void;
  readonly handleCloseDialog: () => void;
}

export default function DispositionDialog({
  open = false,
  visitNoteId,
  patientId,
  isOffsite = false,
  isResolution = false,
  handleCloseDialog,
  handleDischargeClick = () => {},
  handleResolutionSubmit = () => {},
}: DispositionDialogProps) {
  const { dataOfDischargeForm } = useSelector(
    (state: any) => state[componentKey]
  );
  const dispatch = useDispatch();
  const [selectedType, setSelectedType] = useState(
    isResolution
      ? RESOLUTION_OPTIONS[0].value
      : isOffsite
        ? "transferToCliniclocation"
        : "discharged"
  );
  // Resolution options are display-only labels; each maps to an existing
  // disposition workflow. Listening Ear provides no clinical care, so
  // "Transfer to Clinic" routes to the Transfer-to-JHC flow (facility/department
  // transfer, no bundle orders, status "Expected"); every other resolution
  // (Resolved/Referred/Refused) -> discharged.
  const effectiveType = isResolution
    ? selectedType === "Transfer to Clinic"
      ? "transferToJHC"
      : "discharged"
    : selectedType;
  // Both offsite discharge options ("Send home from hospital" and "Discharge to
  // subcamp or campsite") run the same discharge-instruction flow; they only
  // differ by label.
  const isDischargeFlow =
    effectiveType === "discharged" || effectiveType === "dischargeToSubcamp";
  const [dischargeNote, setDischargeNote] = useState("");
  const [dischargeNoteError, setDischargeNoteError] = useState("");
  const [templates, setTemplates] = useState<Template[]>([]);
  const [, setLoading] = useState(false);
  const [bundleModalOpen, setBundleModalOpen] = useState(false);
  // `searchText` drives the visible input; `templateSearch` is the debounced
  // value sent to the backend as `searchValue` to filter discharge instructions.
  const [searchText, setSearchText] = useState("");
  const [templateSearch, setTemplateSearch] = useState("");
  const [searchTimer, setSearchTimer] =
    useState<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        const response = await TemplateService.getTemplates({
          pageNumber: 1,
          limit: 100,
          type: "Note",
          status: "Active",
          searchValue: templateSearch || undefined,
        });

        if (response.data?.success && response.data?.templates) {
          setTemplates(response.data.templates);
        }
      } catch (error) {
        console.error("Error fetching templates:", error);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchTemplates();
    }
  }, [open, templateSearch]);

  const handleClose = () => {
    handleCloseDialog();
    dispatch(setBundleOrderData(null))
  };

  // Clear the shared bundle-order state when this disposition dialog unmounts,
  // so a bundle added on one serial note doesn't leak into the next note's
  // dialog (submit + navigate closes the dialog without going through
  // handleClose).
  useEffect(() => {
    return () => {
      dispatch(setBundleOrderData(null));
    };
  }, [dispatch]);

  // Handle template selection
  const handleTemplateSelection = async (templateId: string) => {
    try {
      if (templateId) {
        const response = await TemplateService.getTemplateById(templateId);
        if (response.data?.success && response.data?.template) {
          setDischargeNote(response.data.template.description || "");
          setDischargeNoteError("");
        }
      } else {
        setDischargeNote("");
      }
    } catch (error) {
      console.error("Error fetching template details:", error);
    }
  };

  // Debounced server-side search. Backend search key is `searchValue`
  // (see TemplateService.getTemplates) — typing re-fetches matching templates.
  const handleTemplateSearchChange = (value: string) => {
    setSearchText(value);
    if (searchTimer) {
      clearTimeout(searchTimer);
    }
    const newTimer = setTimeout(() => {
      setTemplateSearch(value);
    }, 500);
    setSearchTimer(newTimer);
  };

  const handleSubmitClick = (selectedType: string, data: any) => {
    switch (selectedType) {
      case "transferToJHC": {
        const payload1 = {
          apiPayload: {
            patientId: patientId,
            visitNoteId: visitNoteId,
            transferToJHC: true,
            status: "transferToJHC",
            // Receiving facility (any facility, not just JHC); the department
            // below becomes the receiving Type of Care.
            facilityId: data?.transferFacility,
            departmentId: data?.jamboreeHealthCenter,
          },
          dataOfDischargeForm: dataOfDischargeForm,
        };
        dispatch(postTransferPatient(payload1));
        break;
      }
      case "transferOffProperty": {
        const payload2 = {
          apiPayload: {
            patientId: patientId,
            visitNoteId: visitNoteId,
            transferOffProperty: true,
            transport_level: getTransportLevel(data),
            status: "transferOffProperty",
            departmentId: data?.hospital,
          },
          dataOfDischargeForm: dataOfDischargeForm,
          patientId: patientId,
          visitNoteId: visitNoteId,
        };
        dispatch(postTransferPatient(payload2));
        break;
      }
      case "transferToClinicLocation": {
        const payload3 = {
          apiPayload: {
            patientId: patientId,
            visitNoteId: visitNoteId,
            transferToClinic: true,
            status: "transferToClinic",
            campId: data?.basecamp,
            // Offsite Transfers dispositions carry this flag; never sent for
            // the non-offsite Disposition dialog.
            ...(isOffsite && { offsiteTransferDisposition: true }),
          },
          dataOfDischargeForm: dataOfDischargeForm,
          patientId: patientId,
          visitNoteId: visitNoteId,
        };
        dispatch(postTransferPatient(payload3));
        break;
      }
    }
  };

  const handleCreateBundleOrder = () => {
    setBundleModalOpen(true);
  };

  const getTransportLevel = (data: any) => {
    return data?.property === "basicLifeSupport"
      ? "Basic Life Support"
      : data?.property === "advanceLifeSupport"
        ? "Advance Life Support"
        : data?.property === "criticalCareTransport"
          ? // Backend's transport_level enum is misspelled "Crirtical Care
            // Transport"; the value must match it exactly or validation fails.
            "Crirtical Care Transport"
          : data?.property === "otherVehicle"
            ? "Other Vehicle"
            : undefined;
  };

  return (
    <React.Fragment>
      <BootstrapDialog aria-labelledby="customized-dialog-title" open={open}>
        <div className="h-full flex flex-col justify-between">
          <DialogTitle
            sx={{ m: 0, p: 2, fontWeight: 600, fontSize: "18px" }}
            id="customized-dialog-title"
          >
            {isResolution ? "Resolution" : "Disposition"}
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={(theme) => ({
              position: "absolute",
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
          <hr />

          <DialogContent sx={{ height: "fit-content" }}>
            <div className="flex flex-col gap-6">
              {/* ORDER TYPE DROPDOWN */}
              <div>
                <EventEHRRadioButton
                  selectedValue={selectedType}
                  name=""
                  grid={true}
                  labelCustomStyle={{
                    marginLeft: "10px",
                    color: "#565656",
                  }}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    rowGap: "21px",
                  }}
                  optionList={
                    isResolution
                      ? RESOLUTION_OPTIONS
                      : isOffsite
                      ? [
                          {
                            label: "Return to base camp clinic",
                            value: "transferToCliniclocation",
                          },
                          {
                            label: "Send home from hospital",
                            value: "discharged",
                          },
                          {
                            label: "Discharge to subcamp or campsite",
                            value: "dischargeToSubcamp",
                          },
                        ]
                      : [
                          { label: "Discharged", value: "discharged" },
                          {
                            label: "Transfer Off Property",
                            value: "transferOffProperty",
                          },
                          {
                            label: "Admit to Observation",
                            value: "transferToCliniclocation",
                          },
                          {
                            label: "Transfer to JHC or Base Camp",
                            value: "transferToJHC",
                          },
                        ]
                  }
                  handleOnChange={(value) => {
                    setDischargeNote("");
                    setSearchText("");
                    setDischargeNoteError("");
                    setSelectedType(value);
                  }}
                />
              </div>

              {effectiveType === "transferToJHC" && (
                <TransferToJHCForm
                  getFormData={(data) => {
                    handleSubmitClick("transferToJHC", data);
                  }}
                />
              )}
              {selectedType === "transferOffProperty" && (
                <TransferOffPropertiesForm
                  getFormData={(data) => {
                    handleSubmitClick("transferOffProperty", data);
                  }}
                />
              )}
              {effectiveType === "transferToCliniclocation" && (
                <TransferToClinicLocationForm
                  isOffsite={isOffsite}
                  getFormData={(data) => {
                    handleSubmitClick("transferToClinicLocation", data);
                  }}
                />
              )}
              {isDischargeFlow && (
                <>
                  <div className="flex flex-col gap-4">
                    <Heading
                      title="Discharge Instruction"
                      isRequired={true}
                      customStyle={
                        {
                          fontSize: "16px",
                          fontWeight: 500,
                          color: "rgb(86, 86, 86)",
                          // marginBottom: "8px",
                        }
                      }
                    />
                    <SearchSelectDropdown
                      isUpLabelPresent
                      label="Select Discharge Instruction"
                      fullWidth="100%"
                      size="small"
                      searchedValue={searchText}
                      error={Boolean(dischargeNoteError)}
                      helperText={dischargeNoteError}
                      options={templates.map((template) => ({
                        label: template.name,
                        value: template.id,
                      }))}
                      onSearchValueChangeCb={(e) => {
                        handleTemplateSearchChange(e.target.value);
                      }}
                      onSelectSearchValue={(option) => {
                        if (option && typeof option === "object") {
                          setSearchText(option.label);
                          handleTemplateSelection(option.value);
                        } else {
                          setSearchText("");
                          handleTemplateSelection("");
                        }
                      }}
                    />
                    <Input
                      type="text"
                      // label="Discharge Instructions"
                      name="dischargeNote"
                      placeholder="Enter Discharge Instructions"
                      rows={4}
                      multiline={true}
                      value={dischargeNote}
                      onChangeCb={(e) => {
                        setDischargeNote(e.target.value);
                        if (dischargeNoteError) setDischargeNoteError("");
                      }}
                    />
                  </div>

                  <BundleOrderDisplay />

                  {/* ACTION BUTTONS */}
                  <div className="mt-9 text-end">
                    {/* <Button
                      type="button"
                      label="Create Bundle Order"
                      variant="outlined"
                      onClickCb={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleCreateBundleOrder();
                      }}
                      style={{
                        padding: "8px 24px",
                        fontSize: "16px",
                        borderColor: "#1976d2",
                        color: "#1976d2",
                        backgroundColor: "white",
                        marginRight: "12px",
                      }}
                    /> */}
                    <Button
                      type="button"
                      label="Submit"
                      variant="contained"
                      onClickCb={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (dischargeNote.trim() === "") {
                          setDischargeNoteError(
                            "Discharge instruction is required"
                          );
                          return;
                        }
                        setDischargeNoteError("");
                        handleDischargeClick("discharged", dischargeNote);
                      }}
                      style={{
                        padding: "8px 55px",
                        fontWeight: "sans-serif",
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        </div>
      </BootstrapDialog>

      <BundleOrderModal
        open={bundleModalOpen}
        handleClose={() => setBundleModalOpen(false)}
        handleSave={(data) => {
          console.log("Bundle Order Data:", data);
          dispatch(setBundleOrderData(data));
          setBundleModalOpen(false);
        }}
      />
    </React.Fragment>
  );
}
